/// <reference types="@cloudflare/workers-types" />

import type { SessionPayload } from "./crypto";
import type { Micros } from "./micros";
import type { DbClient } from "../functions/lib/db/client";
export type { Micros } from "./micros";
export type { DbClient } from "../functions/lib/db/client";

export type Env = {
  /** Postgres client (D1-shaped API). The middleware constructs it per
      request from HYPERDRIVE/DATABASE_URL and injects it here, so every
      handler and repo keeps reading env.DB unchanged. */
  DB: DbClient;
  /** Hyperdrive binding (production) — the pooled route to Postgres. */
  HYPERDRIVE?: { connectionString: string };
  /** Direct connection string (tests, local dev fallback). */
  DATABASE_URL?: string;
  SESSION_SECRET: string;
  AI: Ai;
  RATE_LIMIT?: KVNamespace;  // abuse control; endpoints fail open when absent
  MIN_SUPPORTED_BUILD?: string;
  APPLE_IAP?: string;
  /** Test-only override of the pinned Apple trust root. Unset in production. */
  APPLE_ROOT_SHA256?: string;
  RC_WEBHOOK_SECRET?: string;
  STRIPE_SECRET_KEY?: string;
  STRIPE_WEBHOOK_SECRET?: string;
  STRIPE_PRICE_MONTHLY?: string;
  STRIPE_PRICE_YEARLY?: string; // native update gate (functions/api/version.ts)
  OPENAI_API_KEY: string;
  GEMINI_API_KEY?: string;
  VAPID_PUBLIC_KEY?: string;
  // Accounts & sign-in (all optional — features degrade when unset)
  RESEND_API_KEY?: string;   // Resend API key for transactional auth emails
  APP_ORIGIN?: string;       // e.g. https://lean.example.com — no trailing slash
  EMAIL_FROM?: string;       // bare address, e.g. no-reply@lean.example.com
  GOOGLE_CLIENT_ID?: string;
  GOOGLE_CLIENT_SECRET?: string;
  APPLE_TEAM_ID?: string;
  APPLE_SERVICE_ID?: string;
  APPLE_KEY_ID?: string;
  APPLE_PRIVATE_KEY?: string; // p8 PEM contents
};

export interface RequestData {
  session?: SessionPayload;
}

export type MealSlot = "breakfast" | "lunch" | "dinner" | "snack";

export type Sex = "male" | "female" | "other";

export type GoalType =
  | "weight_loss" | "cut" | "maintain" | "bulk" | "weight_gain";

export interface Goals {
  daily_calories: number;
  maintenance_calories: number;
  protein_g: number;
  carbs_g: number | null;
  fat_g: number | null;
  target_weight_kg: number;
  start_weight_kg: number;
  step_target: number;
  activity_kcal_target: number;
  water_target_ml: number;
  fiber_target_g: number;
  /** Per-meal calorie split in integer percent; all four set and summing to
   *  100, or all four NULL = automatic (the client's 25/35/30/10 preset). */
  split_breakfast: number | null;
  split_lunch: number | null;
  split_dinner: number | null;
  split_snack: number | null;
  height_cm: number | null;
  age: number | null;
  sex: Sex | null;
  goal_type: GoalType;
  auto_adjust_enabled: boolean;
  /** Optional dated occasion the plan is paced toward (shared/event-goal.ts). */
  event_name: string | null;
  event_date: string | null;
  /** Canonical age source; goals.age stays derived from it. */
  birthday?: string | null;
  nutrition_pref?: string | null;
}

export type GoalAdjustmentTrigger = "auto_weekly" | "manual" | "check_in";

export type GoalAdjustment = {
  id: number;
  applied_at: number;
  window_start: string;
  window_end: string;
  measured_tdee_kcal: number;
  daily_calories_before: number;
  daily_calories_after: number;
  maintenance_calories_before: number;
  maintenance_calories_after: number;
  trigger: GoalAdjustmentTrigger;
  reason: string | null;
};

export interface FoodEntry {
  id: number;
  eaten_on: string;        // YYYY-MM-DD
  eaten_at: number;        // unix seconds
  source: "plan" | "custom" | "quick";
  plan_meal_slot: MealSlot | null;
  name: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g: number | null;
  fiber_g: number | null;
  micros: Micros | null;
}

export interface Measurement {
  id: number;
  measured_on: string;     // YYYY-MM-DD
  weight_kg: number | null;
  waist_cm: number | null;
  steps: number | null;
  /** Apple Health daily active energy — display/coaching only, NEVER budget. */
  active_kcal: number | null;
  sleep_hours: number | null;
  note: string | null;
}

export type DrinkType =
  | "water" | "coffee" | "tea" | "soft_drink" | "milk" | "other";

export interface Drink {
  id: number;
  consumed_on: string;
  consumed_at: number;
  type: DrinkType;
  volume_ml: number;
  kcal: number;
  note: string | null;
}

export type ActivityType =
  | "walk" | "run" | "cycle" | "hike" | "swim" | "strength" | "other";

export type ActivitySource = "manual" | "healthkit";

export interface Activity {
  id: number;
  performed_on: string;
  performed_at: number;
  type: ActivityType;
  duration_min: number;
  distance_km: number | null;
  kcal_burned: number;
  note: string | null;
  /** Imported rows are managed by Apple Health sync: not editable in-app. */
  source: ActivitySource;
  /** Catalog snapshot (2026-08-09): emoji/MET/name as logged; null on old
   *  rows and Health imports. */
  emoji?: string | null;
  met?: number | null;
  activity_name?: string | null;
}

/** One HealthKit workout in a sync payload. */
export interface HealthWorkoutInput {
  hk_id: string;              // HK UUID — idempotency key, ≤64 chars
  type: ActivityType;         // client-mapped from the HK workout type
  start_at: number;           // unix seconds
  duration_min: number;       // ≥1
  distance_km?: number | null;
  kcal_burned: number;        // 0..5000
  note?: string | null;
}

/** One day in a health sync payload. `workouts` PRESENT (even []) makes the
 *  server mirror that day: imported rows missing from the list are removed. */
export interface HealthSyncDay {
  date: string;               // YYYY-MM-DD
  steps?: number;
  active_kcal?: number;       // 0..10000
  workouts?: HealthWorkoutInput[];
}

export interface MacroTotals {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  sugar_g: number;
  fiber_g: number;
  micros: Micros;
}

export type UserRole = "admin" | "member";

export interface User {
  id: number;
  name: string;
  role: UserRole;
  created_at: number;
}

/** A user row as the admin panel sees it: identity + live entitlement. */
export interface AdminUser extends User {
  email: string | null;
  entitlement: Entitlement;
}

export interface PublicUser {
  id: number;
  name: string;
}

export interface MeResponse {
  id: number;
  name: string;
  email: string | null;
  email_verified: boolean;
  role: UserRole;
  providers: ("apple" | "google")[];
  onboarded: boolean;
  /** Unix seconds — the account's timeline starts on this day (shared/timeline.ts). */
  created_at: number;
  last_name: string | null;
  username: string | null;
  city: string | null;
  /** Always sanitized server-side — never null in a response. */
  unit_prefs: import("./units").UnitPrefs;
}

export interface TodayResponse {
  date: string;            // YYYY-MM-DD local
  goals: Goals;
  consumed: MacroTotals;
  burned_kcal: number;
  entries: FoodEntry[];
  activities: Activity[];
  drinks: Drink[];
  consumed_water_ml: number;
  latest_weight_kg: number | null;   // most recent measurements.weight_kg on/before date
  /** The day latest_weight_kg was actually measured, so the UI can say where
   *  a carried-forward number came from instead of implying it is today's. */
  latest_weight_on: string | null;
  /** A weight was recorded on THIS day. latest_weight_kg reaches back; this
   *  does not — the weigh-in reminder needs the difference. */
  weighed_today: boolean;
  steps: number | null;              // the day's measurements.steps (manual or Apple Health)
  active_kcal: number | null;        // the day's Health active energy (display only)
  budget_kcal: number;               // THE daily budget — single rule in functions/lib/today.ts
  user: PublicUser & { role: UserRole };   // NEW — current logged-in user
  workouts: WorkoutSet[];
  pending_check_in: CheckIn | null;
  last_sessions: Record<string, { date: string; weight_kg: number; reps: number }>;
  last_adjustment: GoalAdjustment | null;
  sunday_maintenance: { target_kcal: number } | null;
  refeed_today: {
    week_number: number;
    target_kcal: number;
    milestone: boolean;
  } | null;
  tier?: "free" | "premium";
  cycled_baseline_kcal?: number;
  /** Distinct days with at least one food entry — drives F8 milestones. */
  logged_days?: number;
}

export interface EstimateItem {
  name: string;
  kcal: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  sugar_g?: number;
  micros?: Micros;
  grams?: number;          // estimated weight of this portion
  unit?: "g" | "ml";       // label for the weight; "ml" for liquids
}

export interface EstimateResponse {
  fiber_g?: number | null;
  /** Honest range: sampled spread when 2+ samples agree to disagree, a stated
   *  ±15% heuristic otherwise. Display only — never persisted. */
  kcal_low?: number;
  kcal_high?: number;
  sample_count?: number;
  /** The user's own remembered correction for this food, when one exists. */
  user_prior?: {
    name_key: string; calories: number;
    protein_g: number | null; carbs_g: number | null; fat_g: number | null; sugar_g: number | null;
    times_corrected: number;
  };
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  sugar_g: number | null;
  micros: Micros | null;
  items: EstimateItem[];
}

export type StatsRange = 7 | 30 | 90;

export type StatsResponse = {
  range: StatsRange;
  from: string;
  to: string;
  days_with_data: number;
  weight: {
    start_kg: number | null;
    current_kg: number | null;
    delta_kg: number | null;
    rate_kg_per_week: number | null;
    projection: {
      goal_weight_kg: number;
      eta_date: string | null;
      weeks_to_goal: number | null;
      on_track: boolean;
    } | null;
    series: { date: string; weight_kg: number }[];
  };
  energy_balance: {
    measured_tdee_kcal: number;
    avg_net_kcal: number;
    avg_balance_kcal: number;
  } | null;
  calories: {
    avg_in: number;
    avg_out: number;
    avg_net: number;
    target: number;
    series: { date: string; in: number; out: number }[];
  };
  macros: {
    avg_protein_g: number;
    avg_carbs_g: number;
    avg_fat_g: number;
    protein_target_g: number;
    protein_adherence_pct: number | null;
    protein_pct: number;
    carbs_pct: number;
    fat_pct: number;
    carbs_target_g: number;
    fat_target_g: number;
  };
  water: {
    avg_ml: number;
    target_ml: number;
  };
  consistency: {
    range: number;
    food_days: number;
    current_food_streak: number;
    longest_food_streak: number;
    avg_meals_per_day: number;
  };
  top_foods: { name: string; count: number }[];
  best_day: { date: string; net: number } | null;
  worst_day: { date: string; net: number } | null;
  streak_days: number;
  workout: {
    total_volume_kg: number;
    series: { week_starting: string; volume_kg: number }[];
  };
};

export type WorkoutSet = {
  id: number;
  exercise: string;
  set_number: number;
  reps: number;
  weight_kg: number;
  rpe: number | null;
};

export type WorkoutSetInput = {
  exercise: string;
  reps: number;
  weight_kg: number;
  rpe?: number | null;
};

export type CheckInSuggestion = {
  delta_kcal: number;
  message: string;
};

export type CheckInUserAction = "applied" | "dismissed";

export type CheckIn = {
  id: number;
  week_starting: string;
  avg_weight_kg: number | null;
  weight_delta_kg: number | null;
  avg_calories: number;
  days_with_food: number | null;
  adherence_pct: number;
  suggestion: CheckInSuggestion | null;
  user_action: CheckInUserAction | null;
  created_at: number;
};

export interface FrequentFood {
  name: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g?: number | null;
  micros_json?: string | null;
  count: number;
  last_eaten_on: string; // YYYY-MM-DD
  starred?: boolean;
}

export interface SavedMealItem {
  id: number;
  name: string;
  kcal: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g: number | null;
  micros_json: string | null;
  grams: number | null;
  unit: string | null;
}

export interface SavedMeal {
  id: number;
  name: string;
  emoji: string | null;
  created_at: number;
  items: SavedMealItem[];
}

export interface Cycling {
  enabled: number;
  preset: string | null;
  mon: number; tue: number; wed: number; thu: number;
  fri: number; sat: number; sun: number;
}

export interface Fast {
  id: number;
  protocol: "16:8" | "18:6" | "20:4" | "5:2";
  target_hours: number;
  started_at: number;
  ended_at: number | null;
}

export interface FastsResponse {
  current: Fast | null;
  history: Fast[];
  streak_days: number;
}

/** Server-computed subscription entitlement (functions/lib/entitlements.ts). */
export interface Entitlement {
  tier: "free" | "premium";
  source: "apple" | "stripe" | "admin" | "comp" | null;
  expires_at: number | null;
  will_renew: boolean;
  /** Which plan, so the app can say "Yearly" rather than just "premium". */
  period?: "monthly" | "yearly" | null;
  /** trialing | active | in_grace | billing_issue | canceled — lets the UI
   *  distinguish "free trial ends" from "renews" from "access ends". */
  status?: string | null;
  /** False when no store is configured — hide every upgrade path. */
  billing_enabled?: boolean;
  /** Whether the WEB checkout (Stripe) is actually open. billing_enabled can be
   *  true on Apple alone; a browser must not render buttons that only 503. */
  billing_web_enabled?: boolean;
}

/** A starred food joined with its newest logged macros (null when never logged). */
export interface FavouriteFood {
  name: string;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  sugar_g?: number | null;
  micros_json?: string | null;
  count: number;
  last_eaten_on: string | null;
}

/** Display name for quick-add rows — the UI renders t("quick.name") instead. */
export const QUICK_ADD_NAME = "Quick add";

// The kind union lives in shared/notifications.ts, alongside the rules that
// decide when each one may be sent.
export interface ReminderSetting {
  kind: string;       // ReminderKind — see shared/notifications.ts
  enabled: boolean;
  local_time: string; // "HH:MM"
  tz: string;         // IANA
}

/** The global controls above the per-kind settings. */
export interface ReminderPrefsResponse {
  master: boolean;
  quiet_from: string; // "HH:MM"
  quiet_to: string;   // "HH:MM"
  settings: ReminderSetting[];
}

export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

// --- M2.7 barcode ---

export interface Per100g {
  kcal: number;
  protein_g: number | null;
  carbs_g: number | null;
  sugar_g: number | null;
  fat_g: number | null;
  sat_fat_g: number | null;
  fiber_g: number | null;
  salt_g: number | null;
  micros: Micros | null;
}

export type ServingUnit = "g" | "ml";

export interface NormalizedProduct {
  code: string;
  name: string;
  brand: string;
  image_url: string | null;
  unit: ServingUnit;             // base unit for per_100g & serving_size
  serving_size_g: number | null; // name kept; value is expressed in `unit`
  per_100g: Per100g;
}

export type BarcodeLookupHit = {
  status: "hit";
  source: "cache" | "off";
  fetched_at: number;
  product: NormalizedProduct;
};

export type BarcodeLookupMiss = {
  status: "miss";
  source: "cache" | "off";
  fetched_at: number;
  code: string;
};

// The server is cache-only: when a code is not cached (or its cached miss is
// stale, or ?refresh=1), it returns "uncached" and the BROWSER fetches OFF
// directly, then writes the result back via POST /api/barcode/:code.
// (Cloudflare Worker egress IPs are shared datacentre IPs that OFF blocks /
// rate-limits; the user's device IP is not.)
export type BarcodeLookupUncached = {
  status: "uncached";
  code: string;
};

export type BarcodeLookupResponse =
  | BarcodeLookupHit
  | BarcodeLookupMiss
  | BarcodeLookupUncached;

export interface BarcodeRecentItem {
  code: string;
  last_scanned_at: number;
  scan_count: number;
  product: NormalizedProduct | null;
}

export interface BarcodeRecentsResponse {
  items: BarcodeRecentItem[];
}

// Raw OFF response shape (only the fields we read).
export interface OffNutriments {
  "energy-kcal_100g"?: number | string;
  "energy_100g"?: number | string;
  "proteins_100g"?: number | string;
  "carbohydrates_100g"?: number | string;
  "sugars_100g"?: number | string;
  "fat_100g"?: number | string;
  "saturated-fat_100g"?: number | string;
  "fiber_100g"?: number | string;
  "salt_100g"?: number | string;
  "vitamin-c_100g"?: number | string;
  "vitamin-d_100g"?: number | string;
  "vitamin-a_100g"?: number | string;
  "calcium_100g"?: number | string;
  "iron_100g"?: number | string;
  "potassium_100g"?: number | string;
  "magnesium_100g"?: number | string;
  "zinc_100g"?: number | string;
}

export interface OffProduct {
  product_name?: string;
  product_name_de?: string;
  generic_name?: string;
  generic_name_de?: string;
  brands?: string;
  image_front_url?: string;
  image_front_small_url?: string;
  serving_size?: string;
  nutriments?: OffNutriments;
  nutrition_data_per?: string;
  /** Package size as printed, e.g. "330 ml", "0.5 l", "250 g". */
  quantity?: string;
  product_quantity_unit?: string;
}

export interface OffResponse {
  status?: 0 | 1;
  product?: OffProduct;
}
